
import React from 'react';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/IconButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Edit, Trash2, X, Check, Mail } from 'lucide-react';
import { Member } from '../types/memberTypes';

interface MemberMobileActionsProps {
  member: Member;
  isEditing: boolean;
  canManageBusinessMembers?: boolean;
  handleEditClick: (memberId: string) => void;
  handleCancelEdit: () => void;
  handleSaveEdit: () => void;
  handleRemoveMemberClick: (member: Member) => void;
  handleResendInvite?: (member: Member) => void;
}

const MemberMobileActions: React.FC<MemberMobileActionsProps> = ({
  member,
  isEditing,
  canManageBusinessMembers,
  handleEditClick,
  handleCancelEdit,
  handleSaveEdit,
  handleRemoveMemberClick,
  handleResendInvite
}) => {
  if (isEditing) {
    return (
      <div className="flex space-x-2">
        <IconButton
          icon={<X size={16} />}
          onClick={handleCancelEdit}
          aria-label="Cancel"
          variant="outline"
          className="h-8 w-8 p-0"
        />
        <IconButton
          icon={<Check size={16} />}
          onClick={handleSaveEdit}
          aria-label="Save"
          className="h-8 w-8 p-0"
        />
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 p-0"
        >
          <MoreHorizontal size={16} />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        {/* Show Edit option for non-current members if user can manage members */}
        {!member.isCurrentUser && handleEditClick && canManageBusinessMembers && (
          <DropdownMenuItem onClick={() => handleEditClick(member.id)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
        )}
        
        {/* Show resend invite option for invited members */}
        {member.status === 'invited' && !member.isCurrentUser && handleResendInvite && canManageBusinessMembers && (
          <DropdownMenuItem onClick={() => handleResendInvite(member)}>
            <Mail className="mr-2 h-4 w-4" />
            Resend Invite
          </DropdownMenuItem>
        )}
        
        {/* Show the delete option for non-current members and non-primary owners */}
        {!member.isCurrentUser && member.role !== 'Primary Owner' && handleRemoveMemberClick && canManageBusinessMembers && (
          <>
            {(!member.isCurrentUser && handleEditClick) || 
             (member.status === 'invited' && handleResendInvite) ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem 
              onClick={() => handleRemoveMemberClick(member)}
              className="text-red-500 focus:text-red-500"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Remove
            </DropdownMenuItem>
          </>
        )}
        
        {/* For current member, add view profile option */}
        {member.isCurrentUser && (
          <DropdownMenuItem onClick={() => {}}>
            View Profile
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MemberMobileActions;
